import React from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import Navbar from '../nav/Navbar';
import Footer from '../common/Footer';
import AppLandingPageController from '../../controllers/AppLandingPageController';
import PageContainer from '../RouteContainers/PageContainer';
import RegistrationLandingPage from '../RegistrationLandingPage/RegistrationLandingPage';
import InviteLandingPage from '../Invitations/InviteLandingPage';
import LinkAccountLandingPage from '../Invitations/LinkAccountLandingPage';
import SsoCreate from '../SsoCreate/SsoCreate.container';
import SsoLink from '../SsoLink/SsoLink.container';
import { appStyle } from './styles/App.css';
import {
    loggingOut,
    logoutFailed,
    logoutSuccess,
} from '../../actions/AuthActions';
import GroupDirectory from '../SecurityGroups/directory/GroupDirectory';
import UsersDirectory from '../Users/directory/UsersDirectory';
import SsoConfirm from '../SsoConfirm/SsoConfirm';
import { Modes, PermissionCategories } from '../../constants/Enums';
import Routes from '../../constants/Routes';
import AuthorizedRoute from '../RouteContainers/AuthorizedRoute';
import UserSettingsController from '../../controllers/UserSettingsController';
import SecurityGroupController from '../../controllers/SecurityGroupController';
import AuthenticatedRouteContainer from '../RouteContainers/AuthenticatedRouteContainer';
import ClientSelect from '../ClientSelect/ClientSelect';
import LinkAccountForgotPasswordPage from '../ResetPassword/ResetPasswordPage';
import { getUrlParameter } from '../../functions/urlParams';
import InactiveUserRedirect from '../Login/InactiveUserRedirect';
import Login from '../Login/Login';
import { getToken, getUserFromToken, isAuthenticated } from '../../functions/AuthenticationFunctions';
import { useServiceApi } from '../../hooks/services';
import { authServiceApiFactory } from '../../auth/authServiceApiFactory';
import CacheBuster from '../../cacheBuster';
import { useAuth0 } from '@auth0/auth0-react';

const App = () => {
    const dispatch = useDispatch();
    const authServiceApi = useServiceApi(authServiceApiFactory);
    const { logout } = useAuth0();

    return <CacheBuster>
        {({ loading, isLatestVersion, refreshCacheAndReload }) => {
            if (loading) return null;
            if (!loading && !isLatestVersion) {
                refreshCacheAndReload();
            }

            return  (
                <div id="app" style={appStyle}>
                    <BrowserRouter>
                        <Helmet>
                            <meta
                                name='viewport'
                                content='width=device-width, initial-scale=1, shrink-to-fit=no'
                            />
                        </Helmet>
                        <Switch>
                            <Route
                                exact
                                path={Routes.LOGIN}
                                render={() => <Login />}
                            />
                            <Route
                                exact
                                path={Routes.REGISTRATION.LANDING}
                                render={obj => {
                                    return (
                                        <PageContainer
                                            component={<RegistrationLandingPage match={obj.match} />}
                                            match={obj.match}
                                        />
                                    );
                                }}
                            />
                            <Route
                                exact
                                path={Routes.REGISTRATION.SSO_LINK}
                                render={obj => {
                                    return (
                                        <PageContainer
                                            component={<SsoLink match={obj.match} />}
                                            match={obj.match}
                                        />
                                    );
                                }}
                            />
                            <Route
                                exact
                                path={Routes.REGISTRATION.SSO_CREATE}
                                render={obj => {
                                    return (
                                        <PageContainer
                                            component={<SsoCreate match={obj.match} />}
                                            match={obj.match}
                                        />
                                    );
                                }}
                            />
                            <Route
                                exact
                                path={Routes.REGISTRATION.SSO_CONFIRM}
                                render={obj => {
                                    return (
                                        <PageContainer
                                            component={<SsoConfirm match={obj.match} />}
                                            match={obj.match}
                                        />
                                    );
                                }}
                            />
                            <Route
                                path={Routes.LINK_ACCOUNT}
                                render={() => {
                                    return <LinkAccountLandingPage />
                                }}
                            />
                            <Route
                                path={Routes.RESET_PASSWORD}
                                render={() => {
                                    return <LinkAccountForgotPasswordPage />
                                }}
                            />
                            <Route
                                exact
                                path={Routes.INVITATION}
                                render={() => {
                                    return <InviteLandingPage />
                                }}
                            />
                            <Route exact path={Routes.AUTH_LOGOUT} render={async () => {
                                const user = getUserFromToken();

                                dispatch(loggingOut());
                                if (user.isInternal) {

                                    dispatch(logoutSuccess());
                                    await logout({
                                        logoutParams: {
                                            returnTo: window.location.origin
                                        }
                                    });
                                }
                            }}
                            />

                            <Route exact path={Routes.LOGOUT} render={() => {
                                if (!getToken()) {
                                    dispatch(logoutSuccess());
                                    return <Redirect to={Routes.LOGIN} />
                                } else {
                                    const user = getUserFromToken();

                                    dispatch(loggingOut());
                                    if (user.isInternal) {
                                        return <Redirect to={Routes.AUTH_LOGOUT} />
                                    } else {
                                        const response = authServiceApi.logout();
                                        if (!response.error) {
                                            dispatch(logoutSuccess());
                                            return <Redirect to={Routes.LOGIN} />;
                                        } else {
                                            dispatch(logoutFailed(response.error));
                                        }
                                        return null;
                                    }
                                }
                            }}
                            />
                            <Route
                                exact
                                path={Routes.LOGIN_SUCCESS}
                                render={() => {
                                   if (isAuthenticated) {
                                        return <Redirect to={Routes.LANDING_PAGE} />
                                   }
                                }}
                            />
                            <Route
                                exact
                                path={Routes.CLIENT_SELECT}
                                render={() => <>
                                    <Navbar />
                                    <ClientSelect />
                                </>}
                            />
                            <Route exact path={Routes.INACTIVE_USER_ERROR} render={() => {
                                return <InactiveUserRedirect />
                            }}
                            />
                            <Route exact path={Routes.REDIRECT}
                                   render={() => {
                                       sessionStorage.setItem('redirectAfterLogin', getUrlParameter('url'));
                                       return <Redirect to={Routes.LANDING_PAGE} />
                                   }}
                            />

                            <AuthenticatedRouteContainer>
                                <Route render={() => <Navbar />} />
                                <div className="content mb-0">
                                    <Switch>
                                        <Route
                                            exact path={Routes.LANDING_PAGE}
                                            component={AppLandingPageController} />

                                        <Route exact path={Routes.USER_SETTINGS}
                                               component={UserSettingsController} />
                                        <Route exact path={Routes.USERS}
                                               render={() => {
                                                   return <AuthorizedRoute
                                                           component={<UsersDirectory />}
                                                           permissionCategory={PermissionCategories.USERS} />
                                               }}
                                        />
                                        <Route exact path={Routes.SECURITY_GROUPS}
                                               render={() => {
                                                   return <AuthorizedRoute
                                                            component={<GroupDirectory />}
                                                            permissionCategory={PermissionCategories.ROLES} />
                                               }}
                                        />
                                        <Route exact path={Routes.SECURITY_GROUP_CREATE}
                                               render={() => {
                                                   return <AuthorizedRoute
                                                            component={<SecurityGroupController mode={Modes.CREATE} />}
                                                            permissionCategory={PermissionCategories.ROLES} />
                                               }}
                                        />
                                        <Route exact path={Routes.SECURITY_GROUP_EDIT}
                                               render={() => {
                                                   return <AuthorizedRoute
                                                            component={<SecurityGroupController mode={Modes.EDIT} />}
                                                            permissionCategory={PermissionCategories.ROLES} />
                                               }}
                                        />
                                        <Route exact path={Routes.SECURITY_GROUP_COPY}
                                               render={() => {
                                                   return <AuthorizedRoute
                                                            component={<SecurityGroupController mode={Modes.COPY} />}
                                                            permissionCategory={PermissionCategories.ROLES} />
                                               }}
                                        />
                                        <Route
                                            exact
                                            path={Routes.COMMUNITY_SIGNIN}
                                            render={() => {
                                                window.location = 'https://lwolf.force.com';
                                            }}
                                        />
                                        <Route
                                            render={() => {
                                                return (
                                                    <div className="container content">
                                                        <br />
                                                        <h2>Page Not Found</h2>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </Switch>
                                </div>
                            </AuthenticatedRouteContainer>

                            <Route
                                render={() => {
                                    return (
                                        <div className="container content">
                                            <br />
                                            <h2>Page Not Found</h2>
                                        </div>
                                    );
                                }}
                            />

                        </Switch>
                    </BrowserRouter>
                    <Footer />
                </div>
            );
        }}
    </CacheBuster>
};

export default App;